"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	DEFAULT_PAGES_VISIBILITY,
	type PagesVisibility,
} from "@/types/site-settings";
import { fetchPagesVisibility } from "@/hooks/use-pages-visibility";

type SiteSettingsContextValue = {
	pages: PagesVisibility;
	loading: boolean;
	refresh: () => Promise<void>;
};

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
	pages: DEFAULT_PAGES_VISIBILITY,
	loading: true,
	refresh: async () => {},
});

export function SiteSettingsProvider({
	initialPages,
	children,
}: {
	initialPages: PagesVisibility;
	children: React.ReactNode;
}) {
	const [pages, setPages] = useState(initialPages);
	const [loading, setLoading] = useState(false);

	const refresh = useCallback(async () => {
		setPages(await fetchPagesVisibility());
		setLoading(false);
	}, []);

	useEffect(() => {
		const onChange = () => {
			void refresh();
		};
		window.addEventListener("site-settings:changed", onChange);
		return () => window.removeEventListener("site-settings:changed", onChange);
	}, [refresh]);

	return (
		<SiteSettingsContext.Provider value={{ pages, loading, refresh }}>
			{children}
		</SiteSettingsContext.Provider>
	);
}

export function useSiteSettings() {
	return useContext(SiteSettingsContext);
}
